import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, MessageSquare, BarChart3, Brain, ChevronRight, Lock, BookOpen, GraduationCap, PenLine, HelpCircle, FileText, Check, ChevronDown, Star, Users, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import { SpurwechselLogo } from "@/components/SpurwechselLogo";

const fadeIn = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };

const FAQ_ITEMS = [
  { q: "Ersetzt Spurwechsel die MPU-Beratung?", a: "Nein. Spurwechsel ist ein Übungswerkzeug für das psychologische Gespräch. Es ergänzt die professionelle Beratung durch eine MPU-Stelle — es ersetzt sie nicht." },
  { q: "Sind meine Daten sicher?", a: "Ja. Alle Daten werden verschlüsselt und DSGVO-konform in Europa gespeichert. Sie können Ihr Konto jederzeit vollständig löschen." },
  { q: "Wie oft kann ich üben?", a: "Mit einem bezahlten Plan so oft Sie möchten — ohne Limit. Im kostenlosen Plan erhalten Sie 3 Sessions zum Kennenlernen." },
  { q: "Wie realistisch ist die KI?", a: "Die KI ist auf echte MPU-Gesprächsverläufe trainiert und stellt die Fragen, die auch Gutachter in der MPU stellen. Sie erkennt Ausweichmuster, Widersprüche und gibt konkretes Feedback." },
  { q: "Was kostet Spurwechsel?", a: "3 Sessions sind kostenlos. Danach starten die Pläne ab 39 € (einmalig). Keine monatlichen Gebühren, kein Abo." },
  { q: "Kann ich den Gutachter-Stil wählen?", a: "Ja. Sie können zwischen freundlich, prüfungsnah, kritisch und intensiv wählen — und Ihren Fortschritt mit verschiedenen Stilen testen." },
];

function FaqSection() {
  const [open, setOpen] = useState(null);
  return (
    <section className="py-16" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="font-body text-xs font-medium tracking-[0.1em] uppercase text-primary">Häufige Fragen</span>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-3">Noch Fragen?</h2>
        </div>
        <div className="space-y-2">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} className="border border-border/50 rounded-xl overflow-hidden" data-testid={`faq-item-${i}`}>
              <button className="w-full px-5 py-4 flex items-center justify-between text-left" onClick={() => setOpen(open === i ? null : i)}>
                <span className="font-body text-sm font-medium pr-4">{item.q}</span>
                <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && (
                <div className="px-5 pb-4">
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Nav */}
      <nav className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <SpurwechselLogo size="small" />
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" className="font-body text-sm" data-testid="nav-login-btn">Anmelden</Button></Link>
            <Link to="/register"><Button className="font-body text-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="nav-register-btn"><span className="hidden sm:inline">Kostenlos </span>Starten</Button></Link>
          </div>
        </div>
      </nav>

      {/* Promo */}
      <div className="bg-primary/5 border-b border-primary/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-center gap-3 flex-wrap">
          <Badge className="bg-primary text-primary-foreground font-body text-xs">Launch-Angebot</Badge>
          <p className="font-body text-sm">50% Rabatt mit Code <code className="font-mono text-primary font-bold bg-primary/10 px-1.5 py-0.5 rounded">LAUNCH50</code></p>
          <Button variant="ghost" size="sm" className="font-body text-xs text-primary" onClick={() => navigate("/pricing")} data-testid="promo-cta-btn">
            Angebot ansehen <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>

      {/* ═══ HERO ═══ */}
      <section className="pt-16 pb-12 lg:pt-24 lg:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeIn} className="max-w-3xl mx-auto text-center lg:text-left lg:mx-0">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.08]">
              Klar denken.<br />Sicher sprechen.
            </h1>
            <p className="font-body text-base sm:text-lg text-muted-foreground leading-relaxed mt-6 max-w-xl mx-auto lg:mx-0">
              Spurwechsel bereitet Sie auf das psychologische Gespräch der MPU vor — mit KI-gestützten Übungsgesprächen, ehrlichem Feedback und strukturierter Reflexion.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center lg:justify-start">
              <Button size="lg" className="font-body text-base bg-primary text-primary-foreground hover:bg-primary/90 shadow-md hover:-translate-y-0.5 transition-all duration-300" onClick={() => navigate("/register")} data-testid="hero-cta-btn">
                Kostenlos starten <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button size="lg" variant="outline" className="font-body text-base border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300" onClick={() => navigate("/demo")} data-testid="hero-demo-btn">
                Demo ansehen
              </Button>
            </div>
            <p className="font-body text-xs text-muted-foreground mt-4">Keine Kreditkarte nötig. 3 Sessions kostenlos.</p>
            <div className="flex items-center gap-4 mt-3 justify-center lg:justify-start flex-wrap">
              <span className="flex items-center gap-1 font-body text-xs text-muted-foreground"><Lock className="w-3 h-3" /> DSGVO-konform</span>
              <span className="flex items-center gap-1 font-body text-xs text-muted-foreground"><Shield className="w-3 h-3" /> Diskret & anonym</span>
              <span className="flex items-center gap-1 font-body text-xs text-muted-foreground"><Check className="w-3 h-3" /> Sofort starten</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ SOCIAL PROOF ═══ */}
      <section className="py-10 border-b border-border/50" data-testid="social-proof-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-6 mb-10 max-w-2xl mx-auto text-center">
            {[
              { value: "500+", label: "Übungsgespräche geführt", icon: MessageSquare },
              { value: "92%", label: "fühlen sich besser vorbereitet", icon: TrendingUp },
              { value: "4.8/5", label: "Nutzerbewertung", icon: Star },
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <stat.icon className="w-5 h-5 text-primary mx-auto mb-1.5" />
                <p className="font-heading text-xl sm:text-2xl font-bold text-foreground">{stat.value}</p>
                <p className="font-body text-xs text-muted-foreground">{stat.label}</p>
              </motion.div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {[
              { text: "Ich war total nervös vor der MPU. Nach zwei Wochen mit Spurwechsel wusste ich, worauf der Gutachter achtet. Das hat mir die Angst genommen.", author: "T., 34, Alkoholdelikt" },
              { text: "Die KI stellt genau die Fragen, die auch der echte Gutachter stellt. Dadurch konnte ich meine Antworten üben und verbessern.", author: "M., 28, Drogendelikt" },
            ].map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.1 }}>
                <Card className="border-border/50 h-full">
                  <CardContent className="pt-6 pb-4">
                    <p className="font-body text-sm text-muted-foreground leading-relaxed italic">"{t.text}"</p>
                    <p className="font-body text-xs text-primary font-medium mt-3">— {t.author}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <p className="font-body text-[10px] text-muted-foreground text-center mt-3">Anonymisierte Erfahrungsberichte. Ergebnisse können individuell variieren.</p>
        </div>
      </section>

      {/* ═══ PROBLEM ═══ */}
      <section className="py-12 lg:py-16 border-y border-border/50 bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-8">Sie stehen vor der MPU?</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { icon: HelpCircle, text: "Sie wissen nicht, was der Gutachter hören will" },
                { icon: MessageSquare, text: "Sie sind unsicher, wie Sie Ihre Geschichte erzählen sollen" },
                { icon: Brain, text: "Sie möchten ehrlich antworten, aber richtig vorbereitet sein" },
              ].map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">{item.text}</p>
                </motion.div>
              ))}
            </div>
            <p className="font-body text-sm text-foreground mt-8 font-medium">Genau dafür gibt es Spurwechsel.</p>
          </div>
        </div>
      </section>

      {/* ═══ LÖSUNG ═══ */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="font-body text-xs font-medium tracking-[0.1em] uppercase text-primary">Was Spurwechsel macht</span>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-3">Üben Sie das Gespräch, bevor es zählt</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: GraduationCap, title: "Realistische Simulation", desc: "Führen Sie Übungsgespräche mit verschiedenen Gutachter-Persönlichkeiten." },
              { icon: Brain, title: "Trainings-Analyse", desc: "Erkennung von Ausweichmustern und Widersprüchen in Ihren Antworten." },
              { icon: PenLine, title: "Geschichte aufarbeiten", desc: "Schritt-für-Schritt-Reflexion für Ihre stimmige Darstellung." },
              { icon: FileText, title: "Akten besprechen", desc: "Laden Sie Ihre Unterlagen hoch — die KI bespricht jeden Punkt mit Ihnen." },
            ].map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <Card className="border-border/50 h-full hover:shadow-md hover:border-primary/20 transition-all duration-300" data-testid={`feature-card-${i}`}>
                  <CardContent className="pt-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                      <f.icon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-heading text-base font-semibold mb-1">{f.title}</h3>
                    <p className="font-body text-sm text-muted-foreground">{f.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ WIE ES FUNKTIONIERT ═══ */}
      <section className="py-16 bg-muted/20 border-y border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-body text-xs font-medium tracking-[0.1em] uppercase text-primary">So funktioniert es</span>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-3">In drei Schritten zur Vorbereitung</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { num: "1", title: "Situation erfassen", desc: "Beschreiben Sie Ihren MPU-Anlass. Spurwechsel passt das Training an Ihre Situation an." },
              { num: "2", title: "Gespräch üben", desc: "Führen Sie realistische Übungsgespräche — so oft Sie möchten, in Ihrem Tempo." },
              { num: "3", title: "Feedback erhalten", desc: "Nach jedem Gespräch erhalten Sie konkretes Trainings-Feedback und nächste Schritte." },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }} className="text-center" data-testid={`step-${i}`}>
                <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4">
                  <span className="font-heading text-xl font-bold">{s.num}</span>
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2">{s.title}</h3>
                <p className="font-body text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ VERTRAUEN ═══ */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-border/50 overflow-hidden">
            <CardContent className="p-8 lg:p-12">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="font-heading text-2xl font-bold tracking-tight mb-4">Ehrlich. Strukturiert. Vertraulich.</h2>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed mb-6">
                    Spurwechsel ist ein Übungswerkzeug für das psychologische MPU-Gespräch.
                    Kein Gutachten, keine Garantie, keine Therapie — sondern ehrliche Vorbereitung mit konkretem Feedback.
                  </p>
                  <div className="space-y-3">
                    {[
                      { icon: Lock, text: "Ihre Daten sind verschlüsselt und DSGVO-konform geschützt" },
                      { icon: Shield, text: "Kein Heilversprechen — ehrliche Trainings-Hilfe" },
                      { icon: BookOpen, text: "Kein Therapie- oder Rechtsberatungsersatz" },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <item.icon className="w-4 h-4 text-primary flex-shrink-0" />
                        <p className="font-body text-sm">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-muted/30 rounded-2xl p-6">
                  <p className="font-body text-xs font-medium text-primary mb-3 tracking-wide uppercase">Für wen ist Spurwechsel?</p>
                  <div className="space-y-2.5">
                    {[
                      "MPU wegen Alkohol, Drogen, Punkten oder Straftaten",
                      "Erstmalige MPU-Vorbereitung",
                      "Nach einer nicht bestandenen MPU",
                      "Begleitend zur professionellen MPU-Beratung",
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                        <p className="font-body text-sm text-foreground">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ═══ KOSTENVERGLEICH ═══ */}
      <section className="py-12 bg-muted/20 border-y border-border/50" data-testid="cost-comparison-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-6">Professionelle Vorbereitung — ein Bruchteil der Kosten</h2>
          <div className="grid sm:grid-cols-2 gap-4 max-w-xl mx-auto">
            <Card className="border-border/50">
              <CardContent className="pt-6 pb-4 text-center">
                <p className="font-body text-xs text-muted-foreground mb-1">Klassische MPU-Beratung</p>
                <p className="font-heading text-2xl font-bold text-muted-foreground line-through">1.000 – 3.000 €</p>
                <p className="font-body text-xs text-muted-foreground mt-1">5–10 Termine nötig</p>
              </CardContent>
            </Card>
            <Card className="border-primary/30 ring-2 ring-primary/20">
              <CardContent className="pt-6 pb-4 text-center">
                <p className="font-body text-xs text-primary font-medium mb-1">Spurwechsel PRO</p>
                <p className="font-heading text-2xl font-bold text-primary">ab 39 €</p>
                <p className="font-body text-xs text-muted-foreground mt-1">Unbegrenzt üben, jederzeit</p>
              </CardContent>
            </Card>
          </div>
          <p className="font-body text-xs text-muted-foreground mt-4">Ergänzt Ihre MPU-Beratung — ersetzt sie nicht.</p>
        </div>
      </section>

      {/* ═══ RATGEBER ═══ */}
      <section className="py-12" data-testid="guides-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="font-body text-xs font-medium tracking-[0.1em] uppercase text-primary">Kostenlose Ratgeber</span>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-3">MPU-Wissen — kostenlos</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { slug: "mpu-vorbereitung", title: "MPU Vorbereitung 2026", desc: "Ablauf, Kosten & Tipps" },
              { slug: "psychologisches-gespraech", title: "Das psychologische Gespräch", desc: "Ablauf, Fragen, Tipps" },
              { slug: "mpu-fragen", title: "Typische MPU-Fragen", desc: "Die 20 häufigsten Fragen" },
              { slug: "mpu-bestehen-tipps", title: "MPU bestehen", desc: "Bewährte Strategien" },
            ].map((a, i) => (
              <Link key={i} to={`/wissen/${a.slug}`} className="p-4 rounded-xl border border-border/50 hover:border-primary/30 hover:shadow-sm transition-all group">
                <p className="font-body text-sm font-medium group-hover:text-primary">{a.title}</p>
                <p className="font-body text-xs text-muted-foreground mt-0.5">{a.desc}</p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/wissen" className="font-body text-sm text-primary hover:underline">Alle 13 Ratgeber ansehen →</Link>
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <FaqSection />

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-16 bg-primary">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">Bereit für Ihren Spurwechsel?</h2>
          <p className="font-body text-base text-white/80 mb-8">Starten Sie jetzt mit Ihrer Vorbereitung. Kostenlos und unverbindlich.</p>
          <Button size="lg" variant="secondary" className="font-body text-base bg-white text-primary hover:bg-white/90 shadow-lg" onClick={() => navigate("/register")} data-testid="cta-register-btn">
            Kostenlos starten <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="border-t border-border/50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <SpurwechselLogo size="xs" />
              <div className="flex gap-3 flex-wrap">
                <Link to="/impressum" className="font-body text-xs text-muted-foreground hover:text-primary">Impressum</Link>
                <Link to="/privacy" className="font-body text-xs text-muted-foreground hover:text-primary">Datenschutz</Link>
                <Link to="/agb" className="font-body text-xs text-muted-foreground hover:text-primary">AGB</Link>
                <Link to="/b2b" className="font-body text-xs text-muted-foreground hover:text-primary">Für Berater</Link>
                <Link to="/wissen" className="font-body text-xs text-muted-foreground hover:text-primary">MPU-Wissen</Link>
                <Link to="/kontakt" className="font-body text-xs text-muted-foreground hover:text-primary">Kontakt</Link>
              </div>
            </div>
            <p className="font-body text-xs text-muted-foreground text-center sm:text-right">
              Übungswerkzeug — ersetzt keine professionelle MPU-Beratung oder Therapie.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
